import { ArrowRight, ChevronDown, Plus, Search, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ENTITY_ICON_COMPONENTS, LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { graphApi } from '@/services/api/graph'
import type {
  ActiveGraphFilter,
  GraphQuery,
  GraphQueryRelStep,
  GraphQueryStep,
} from '@/types/graph-query'
import { cn } from '@/utils/cn'

interface GraphQueryBuilderProps {
  caseId: string
  entityTypes: Array<{ label: string; count: number }>
  onAddFilter?: (filter: ActiveGraphFilter) => void
}

type BuilderStage = 'idle' | 'entity-select' | 'search-input' | 'chain-add'

interface ChainStep {
  relationshipType?: string
  targetEntityType?: string
  targetSearchTerm?: string
}

export function GraphQueryBuilder({ caseId, entityTypes, onAddFilter }: GraphQueryBuilderProps) {
  const [_stage, setStage] = useState<BuilderStage>('idle')
  const [selectedEntityType, setSelectedEntityType] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [chainSteps, setChainSteps] = useState<ChainStep[]>([])
  const [relationshipTypes, setRelationshipTypes] = useState<
    Array<{ rel_type: string; count: number }>
  >([])
  const [relTypesLoaded, setRelTypesLoaded] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Fetch relationship types lazily on first interaction
  const loadRelTypes = useCallback(async () => {
    if (relTypesLoaded || !caseId) return
    try {
      const data = await graphApi.getRelationshipTypes(caseId)
      setRelationshipTypes(data)
      setRelTypesLoaded(true)
    } catch {
      // silently fail
    }
  }, [caseId, relTypesLoaded])

  // Load rel types when entity is selected (for chain steps)
  useEffect(() => {
    if (selectedEntityType) loadRelTypes()
  }, [selectedEntityType, loadRelTypes])

  const resetBuilder = useCallback(() => {
    setSelectedEntityType(null)
    setSearchTerm('')
    setChainSteps([])
    setStage('idle')
  }, [])

  const handleEntitySelect = useCallback((label: string) => {
    setSelectedEntityType(label)
    setStage('search-input')
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [])

  const handleRemoveEntityType = useCallback(() => {
    setSelectedEntityType(null)
    setChainSteps([])
    setStage('idle')
  }, [])

  const handleAddChainStep = useCallback(() => {
    setChainSteps(prev => [...prev, {}])
    setStage('chain-add')
  }, [])

  const updateChainStep = useCallback((index: number, updates: Partial<ChainStep>) => {
    setChainSteps(prev => prev.map((s, i) => (i === index ? { ...s, ...updates } : s)))
  }, [])

  const removeChainStep = useCallback(
    (index: number) => {
      setChainSteps(prev => prev.filter((_, i) => i !== index))
      if (chainSteps.length <= 1) setStage('search-input')
    },
    [chainSteps.length]
  )

  const buildLabel = (entityType: string | null, term: string, chain: ChainStep[]): string => {
    const parts: string[] = []
    if (entityType) {
      parts.push(entityType)
      if (term) parts[parts.length - 1] += ` "${term}"`
    } else if (term) {
      return `Search: "${term}"`
    }
    for (const step of chain) {
      if (step.targetEntityType) {
        const rel = step.relationshipType || '*'
        let target = step.targetEntityType
        if (step.targetSearchTerm) target += ` "${step.targetSearchTerm}"`
        parts.push(`-[${rel}]-> ${target}`)
      }
    }
    return parts.join(' ') || 'Search'
  }

  const handleSubmit = useCallback(() => {
    if (!selectedEntityType && !searchTerm.trim()) return

    // Plain text search
    if (!selectedEntityType && searchTerm.trim()) {
      onAddFilter?.({
        id: crypto.randomUUID(),
        searchTerm: searchTerm.trim(),
      })
      resetBuilder()
      return
    }

    // Pattern query
    const origin: GraphQueryStep = {
      entityType: selectedEntityType!,
      searchTerm: searchTerm.trim() || undefined,
    }

    const validChain: GraphQueryRelStep[] = chainSteps
      .filter(s => s.targetEntityType)
      .map(s => ({
        relationshipType: s.relationshipType || undefined,
        target: {
          entityType: s.targetEntityType!,
          searchTerm: s.targetSearchTerm || undefined,
        },
      }))

    const query: GraphQuery = {
      id: crypto.randomUUID(),
      label: buildLabel(selectedEntityType, searchTerm, chainSteps),
      origin,
      chain: validChain,
    }

    onAddFilter?.(query)
    resetBuilder()
  }, [selectedEntityType, searchTerm, chainSteps, onAddFilter, resetBuilder, buildLabel])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSubmit()
      }
      if (e.key === 'Escape') {
        resetBuilder()
      }
    },
    [handleSubmit, resetBuilder]
  )

  return (
    <div onKeyDown={handleKeyDown}>
      {/* Search input bar */}
      <div className="flex items-center gap-1 px-2 py-1.5 rounded border border-border bg-background">
        <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        {selectedEntityType && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium text-white shrink-0"
            style={{
              backgroundColor: LABEL_COLORS[selectedEntityType]?.fill || '#64748b',
            }}
          >
            {(() => {
              const Icon = ENTITY_ICON_COMPONENTS[selectedEntityType]
              return Icon ? <Icon className="h-3 w-3" /> : null
            })()}
            {selectedEntityType}
            <button
              onClick={handleRemoveEntityType}
              className="ml-0.5 hover:opacity-70 max-lg:-my-2 max-lg:-mr-1.5 max-lg:p-2"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        )}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder={
            selectedEntityType
              ? `Search within ${selectedEntityType}...`
              : 'Type to search or pick entity type below...'
          }
          className="flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground max-lg:min-w-0 max-lg:placeholder:text-sm lg:text-xs"
        />
        {searchTerm && !selectedEntityType && (
          <Button
            variant="ghost"
            size="sm"
            className="h-5 px-2 text-[11px] shrink-0 max-lg:h-8 max-lg:px-3 max-lg:text-xs"
            onClick={handleSubmit}
          >
            Go
          </Button>
        )}
      </div>

      {/* Entity type grid (shown when no entity is selected) */}
      {!selectedEntityType && (
        <div className="mt-1.5">
          <div className="flex flex-wrap gap-1 max-lg:gap-1.5">
            {entityTypes.map(et => {
              const Icon = ENTITY_ICON_COMPONENTS[et.label]
              const color = LABEL_COLORS[et.label]
              return (
                <button
                  key={et.label}
                  onClick={() => handleEntitySelect(et.label)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] border border-border hover:border-foreground/30 transition-colors max-lg:px-2.5 max-lg:py-1.5 max-lg:text-xs"
                  style={{
                    color: color?.fill || '#64748b',
                  }}
                >
                  {Icon && <Icon className="h-3 w-3" />}
                  <span>{et.label}</span>
                  <span className="opacity-50">{et.count}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Chain steps */}
      {selectedEntityType && chainSteps.length > 0 && (
        <div className="mt-1.5 space-y-1.5">
          {chainSteps.map((step, i) => (
            <ChainStepRow
              key={i}
              step={step}
              index={i}
              entityTypes={entityTypes}
              relationshipTypes={relationshipTypes}
              onUpdate={updateChainStep}
              onRemove={removeChainStep}
            />
          ))}
        </div>
      )}

      {/* Actions bar */}
      {selectedEntityType && (
        <div className="flex items-center gap-2 mt-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-5 px-2 text-[11px] gap-1 max-lg:h-8 max-lg:text-xs"
            onClick={handleAddChainStep}
          >
            <Plus className="h-3 w-3" />
            Add relationship
          </Button>
          <div className="flex-1" />
          <Button
            size="sm"
            className="h-5 px-3 text-[11px] max-lg:h-8 max-lg:px-4 max-lg:text-xs"
            onClick={handleSubmit}
          >
            Search
          </Button>
        </div>
      )}
    </div>
  )
}

// --- Chain step sub-component ---

interface ChainStepRowProps {
  step: ChainStep
  index: number
  entityTypes: Array<{ label: string; count: number }>
  relationshipTypes: Array<{ rel_type: string; count: number }>
  onUpdate: (index: number, updates: Partial<ChainStep>) => void
  onRemove: (index: number) => void
}

function ChainStepRow({
  step,
  index,
  entityTypes,
  relationshipTypes,
  onUpdate,
  onRemove,
}: ChainStepRowProps) {
  const [relOpen, setRelOpen] = useState(false)
  const [targetOpen, setTargetOpen] = useState(false)

  return (
    <div className="flex items-center gap-1.5 text-xs max-lg:flex-wrap max-lg:gap-y-2">
      <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />

      {/* Relationship type selector */}
      <Popover open={relOpen} onOpenChange={setRelOpen}>
        <PopoverTrigger asChild>
          <button className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border hover:border-foreground/30 text-xs min-w-[80px] max-lg:py-2">
            <span className={step.relationshipType ? 'text-foreground' : 'text-muted-foreground'}>
              {step.relationshipType || 'Any rel'}
            </span>
            <ChevronDown className="h-3 w-3 ml-auto text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-2 max-h-[200px] overflow-y-auto" align="start">
          <button
            className="w-full text-left px-2 py-1 rounded text-xs hover:bg-accent"
            onClick={() => {
              onUpdate(index, { relationshipType: undefined })
              setRelOpen(false)
            }}
          >
            <span className="text-muted-foreground">Any relationship</span>
          </button>
          {relationshipTypes.map(rt => (
            <button
              key={rt.rel_type}
              className={cn(
                'w-full text-left px-2 py-1 rounded text-xs hover:bg-accent',
                step.relationshipType === rt.rel_type && 'bg-accent'
              )}
              onClick={() => {
                onUpdate(index, { relationshipType: rt.rel_type })
                setRelOpen(false)
              }}
            >
              {rt.rel_type}
              <span className="ml-1 text-muted-foreground">({rt.count})</span>
            </button>
          ))}
        </PopoverContent>
      </Popover>

      <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />

      {/* Target entity type selector */}
      <Popover open={targetOpen} onOpenChange={setTargetOpen}>
        <PopoverTrigger asChild>
          <button className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border hover:border-foreground/30 text-xs min-w-[80px] max-lg:py-2">
            {step.targetEntityType ? (
              <span
                style={{
                  color: LABEL_COLORS[step.targetEntityType]?.fill || '#64748b',
                }}
              >
                {step.targetEntityType}
              </span>
            ) : (
              <span className="text-muted-foreground">Target type</span>
            )}
            <ChevronDown className="h-3 w-3 ml-auto text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-2 max-h-[200px] overflow-y-auto" align="start">
          {entityTypes.map(et => {
            const Icon = ENTITY_ICON_COMPONENTS[et.label]
            const color = LABEL_COLORS[et.label]
            return (
              <button
                key={et.label}
                className={cn(
                  'w-full text-left px-2 py-1 rounded text-xs hover:bg-accent flex items-center gap-1',
                  step.targetEntityType === et.label && 'bg-accent'
                )}
                onClick={() => {
                  onUpdate(index, { targetEntityType: et.label })
                  setTargetOpen(false)
                }}
              >
                {Icon && <Icon className="h-3 w-3" style={{ color: color?.fill }} />}
                {et.label}
                <span className="ml-auto text-muted-foreground">{et.count}</span>
              </button>
            )
          })}
        </PopoverContent>
      </Popover>

      {/* Target search term */}
      {step.targetEntityType && (
        <input
          type="text"
          value={step.targetSearchTerm || ''}
          onChange={e => onUpdate(index, { targetSearchTerm: e.target.value })}
          placeholder="name..."
          className="w-[80px] bg-transparent text-base outline-none border-b border-border focus:border-foreground px-1 py-0.5 max-lg:min-w-0 max-lg:flex-1 lg:text-xs"
        />
      )}

      <button
        onClick={() => onRemove(index)}
        className="text-muted-foreground hover:text-destructive shrink-0 max-lg:p-2"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}
